<script setup lang="ts">
import { readMateSpritePage } from "@/apis/script-game";
import { useMate } from "@/stores/mate";
import { MateSprite } from "@/types/game";
import { showDialog } from "vant";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useName } from "@/use/common";
import { useRouter } from "vue-router";
import { MateType } from "@/enums/mateEnums";

// Props & Emit
const props = withDefaults(
  defineProps<{
    title?: string;
    show: boolean;
    selectedSpriteIds?: number[];
    maxCount?: number;
    enableEmpty?: boolean;
    showMateWithoutSprite?: boolean;
  }>(),
  {
    selectedSpriteIds: () => [],
    maxCount: 8,
    enableEmpty: false,
    showMateWithoutSprite: false,
  }
);

const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
  (e: "onConfirm", selectedSpriteIds: number[]): void;
}>();

const MAX_COUNT = 8;

const mateStore = useMate();
const { t } = useI18n();
const router = useRouter();

const spriteList = ref<MateSprite[]>([]);
const mateId2SpriteList = computed(() => {
  // 1. 先获取mateId Set. 并根据mateType排序
  const mateIdSet = spriteList.value
    .filter(
      (item) => item.animationzz?.length && item.animationzz[0].media?.url
    )
    .reduce((acc, cur) => {
      acc.add(cur.mateId!);
      return acc;
    }, new Set<number>());
  // 2. 自设置顶排序, 使用Map数据结构，保证keys获取的顺序
  const map = Array.from(mateIdSet)
    .sort((a, b) => {
      const aMate = mateStore.mateIdMap.get(a);
      const bMate = mateStore.mateIdMap.get(b);
      // mateType 3,4 在前，其他在后，3,4 内部按 mateId 排序
      if (aMate?.type === 3 || aMate?.type === 4) {
        return -1;
      }
      if (bMate?.type === 3 || bMate?.type === 4) {
        return 1;
      }
      return a - b;
    })
    .reduce((cur, mateId) => {
      cur.set(
        mateId,
        spriteList.value.filter(
          (item) =>
            item.mateId === mateId &&
            item.animationzz?.length &&
            item.animationzz[0].media?.url
        )
      );
      return cur;
    }, new Map<number, MateSprite[]>());

  return map;
});

const matesWithoutSprite = computed(() => {
  return mateStore.fullListWithSelfAvatar.filter(
    (item) => !Array.from(mateId2SpriteList.value.keys()).includes(item.id)
  );
});

const spriteId2MateId = computed(() => {
  return spriteList.value.reduce((acc, cur) => {
    acc[cur.id!] = cur.mateId!;
    return acc;
  }, {} as Record<number, number>);
});

const mateId2SelectedSpriteId = computed(() => {
  return newSelectedSpriteIds.value.reduce((acc, cur) => {
    acc[spriteId2MateId.value[cur]] = cur;
    return acc;
  }, {} as Record<number, number>);
});

const newSelectedSpriteIds = ref<number[]>(props.selectedSpriteIds);

onMounted(async () => {
  const { page } = await readMateSpritePage({
    pagination: {
      page: 1,
      perPage: 999,
    },
  });
  spriteList.value = page.data;
});

// Methods
const onConfirm = () => {
  emit("update:show", false);
  emit("onConfirm", newSelectedSpriteIds.value);
};

const onSpriteClick = (spriteId: number) => {
  const targetMateId = spriteId2MateId.value[spriteId];
  const isSelected = newSelectedSpriteIds.value.includes(spriteId);
  if (isSelected) {
    newSelectedSpriteIds.value = newSelectedSpriteIds.value.filter(
      (id) => id !== spriteId
    );
  } else {
    const oldSpriteId = mateId2SelectedSpriteId.value[targetMateId];
    if (oldSpriteId) {
      newSelectedSpriteIds.value = newSelectedSpriteIds.value.filter(
        (id) => id !== oldSpriteId
      );
    }
    if (newSelectedSpriteIds.value.length >= MAX_COUNT) {
      showDialog({
        title: t("house.maxCharacterCount", { count: MAX_COUNT }),
        message: t("house.cancelSelectedCharacter"),
      });
      return;
    }
    newSelectedSpriteIds.value.push(spriteId);
  }
};
</script>
<template>
  <VanPopup
    :show="show"
    @update:show="emit('update:show', $event)"
    position="bottom"
    round
    class="h-[80%] grid grid-rows-[auto_auto_1fr] overflow-hidden"
  >
    <ThreadsNav show-back :go-back="() => emit('update:show', false)">
      <template #center>
        <div>{{ title ?? $t("Titles.mymates") }}</div>
      </template>
      <template #right>
        <button
          class="btn btn-primary btn-xs rounded"
          :disabled="!enableEmpty && newSelectedSpriteIds.length === 0"
          @click="onConfirm"
        >
          {{ $t("Buttons.confirm") }}
        </button>
      </template>
    </ThreadsNav>

    <div class="flex flex-col gap-0.5">
      <div class="flex justify-between items-start px-2">
        <div>
          <slot name="tip" :new-selected-sprite-ids="newSelectedSpriteIds" />
        </div>
        <div class="flex items-center text-base-content/50">
          {{ $t("house.restAvaiableCharacter") }}：
          <div>{{ newSelectedSpriteIds.length }} / {{ maxCount }}</div>
        </div>
      </div>
      <slot name="tip2" />
    </div>
    <div class="w-full h-full overflow-y-auto px-2 pb-4">
      <div class="flex flex-col gap-4">
        <div
          v-for="mateId in Array.from(mateId2SpriteList.keys())"
          :key="mateId"
          class="flex flex-col gap-2"
        >
          <div class="flex gap-2 items-center">
            <RedotTitle
              :title="useName(mateStore.mateIdMap.get(Number(mateId))!.name!)"
              size="sm"
              class="ml-4"
            />
            <div
              v-if="
                [MateType.SelfAvatar, MateType.User].includes(
                  mateStore.mateIdMap.get(Number(mateId))?.type ??
                    MateType.Original
                )
              "
              class="badge rounded badge-sm"
            >
              {{ $t("MateType.4") }}
            </div>
          </div>
          <div class="gap-2 grid grid-cols-4 xs:grid-cols-6 md:grid-cols-8">
            <div
              v-for="sprite in mateId2SpriteList.get(mateId)"
              :key="sprite.id"
              class="w-full aspect-square flex items-center justify-center border rounded"
              :class="{
                'border-primary': newSelectedSpriteIds.includes(sprite.id!),
              }"
              @click="onSpriteClick(sprite.id!)"
            >
              <img
                v-if="
                  sprite.imageMedia?.url || sprite.animationzz?.[0]?.media?.url
                "
                :src="
                  sprite.imageMedia?.url ?? sprite.animationzz?.[0]?.media?.url
                "
                class="w-full h-full object-contain"
              />
              <div class="p-2 w-full h-full" v-else>
                <ImgFrameAvatarPlaceholder
                  class="text-secondary/50 object-contain w-full h-full"
                />
              </div>
            </div>
          </div>
        </div>
        <template v-if="matesWithoutSprite.length">
          <div class="divider">{{ $t("house.mateWithoutSprite") }}</div>
          <div
            v-for="mate in matesWithoutSprite"
            :key="mate.id"
            class="flex flex-col gap-2"
          >
            <div class="flex gap-2 items-center">
              <RedotTitle :title="useName(mate.name!)" size="sm" class="ml-4" />
              <div
                v-if="
                  [MateType.SelfAvatar, MateType.User].includes(
                    mate.type ?? MateType.Original
                  )
                "
                class="badge rounded badge-sm"
              >
                {{ $t("MateType.4") }}
              </div>
            </div>
            <div class="gap-2 grid grid-cols-4 xs:grid-cols-6 md:grid-cols-8">
              <div
                class="w-full aspect-square flex items-center justify-center border rounded p-2 relative cursor-pointer"
                @click="
                  router.push({
                    name: 'mateEdit',
                    params: { uuid: mate.uuid },
                  })
                "
              >
                <ImgFrameAvatarPlaceholder
                  class="text-secondary/50 object-contain w-full h-full"
                />
                <div class="bg-base-100 opacity-80 absolute inset-0"></div>
                <div class="absolute inset-0 flex items-center justify-center">
                  <i class="iconify mingcute--add-circle-line"></i>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </VanPopup>
</template>
